import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../components/Admin/AdminContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  MapPin, Search, MoreHorizontal, Eye, EyeOff, 
  Edit, Trash2, Calendar, User, Heart, MessageCircle,
  Image as ImageIcon, Navigation, Car, Clock, Users
} from 'lucide-react';

export default function AdminSpots() {
  const { adminAPI } = useAdmin();
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [showSpotModal, setShowSpotModal] = useState(false);
  const [editingSpot, setEditingSpot] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [spotsPerPage] = useState(20);

  useEffect(() => {
    loadSpots();
  }, [currentPage, searchTerm, statusFilter]);

  const loadSpots = async () => {
    try {
      setLoading(true);
      const params = {
        skip: currentPage * spotsPerPage,
        limit: spotsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status_filter: statusFilter })
      };
      
      const data = await adminAPI.getSpots(params);
      setSpots(data);
    } catch (error) {
      console.error('Failed to load spots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSpotAction = async (action, spotId, data = {}) => {
    try {
      setActionLoading(true);
      
      switch (action) {
        case 'disable':
          await adminAPI.disableSpot(spotId, data.reason);
          break;
        case 'enable':
          await adminAPI.enableSpot(spotId);
          break;
        case 'delete':
          await adminAPI.deleteSpot(spotId);
          break;
        case 'update':
          await adminAPI.updateSpot(spotId, data);
          break;
        default:
          throw new Error('Action non reconnue');
      }
      
      await loadSpots();
      setShowSpotModal(false);
      setEditingSpot(null);
      
    } catch (error) {
      console.error(`Failed to ${action} spot:`, error);
      alert(`Erreur lors de l'action: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { class: 'bg-green-100 text-green-800', label: 'Actif' },
      inactive: { class: 'bg-yellow-100 text-yellow-800', label: 'Inactif' },
      under_review: { class: 'bg-blue-100 text-blue-800', label: 'En révision' },
      deleted: { class: 'bg-red-100 text-red-800', label: 'Supprimé' }
    };
    
    const variant = variants[status] || variants.active;
    return <Badge className={variant.class}>{variant.label}</Badge>;
  };

  const SpotActionModal = ({ spot, onAction }) => {
    const [actionType, setActionType] = useState('');
    const [reason, setReason] = useState('');

    const handleSubmit = (e) => {
      e.preventDefault();
      onAction(actionType, spot.id, { reason });
    };

    return (
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Actions sur le spot</DialogTitle>
          <DialogDescription>
            Spot: {spot.title} (par {spot.author})
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Action à effectuer</Label>
            <Select value={actionType} onValueChange={setActionType}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir une action" />
              </SelectTrigger>
              <SelectContent>
                {spot.spot_status === 'active' && (
                  <SelectItem value="disable">Désactiver</SelectItem>
                )}
                {spot.spot_status !== 'active' && spot.spot_status !== 'deleted' && (
                  <SelectItem value="enable">Réactiver</SelectItem>
                )}
                <SelectItem value="delete">Supprimer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(actionType === 'disable' || actionType === 'delete') && (
            <div>
              <Label>Raison {actionType === 'disable' ? '(obligatoire)' : '(optionnel)'}</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Expliquez la raison de cette action"
                required={actionType === 'disable'}
              />
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button 
              type="submit" 
              disabled={!actionType || actionLoading}
              variant={actionType === 'delete' ? 'destructive' : 'default'}
              className="flex-1"
            >
              {actionLoading ? 'En cours...' : 'Confirmer'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowSpotModal(false)}>
              Annuler
            </Button>
          </div>
        </form>
      </DialogContent>
    );
  };

  const SpotEditModal = ({ spot, onSave }) => {
    const [editData, setEditData] = useState({
      title: spot.title,
      description: spot.description,
      latitude: spot.latitude,
      longitude: spot.longitude,
      parking_latitude: spot.parking_latitude || '',
      parking_longitude: spot.parking_longitude || '',
      tags: spot.tags.join(', '),
      author: spot.author,
      is_anonymous: spot.is_anonymous
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      const updateData = {
        ...editData,
        tags: editData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        parking_latitude: editData.parking_latitude ? parseFloat(editData.parking_latitude) : null,
        parking_longitude: editData.parking_longitude ? parseFloat(editData.parking_longitude) : null,
      };
      onSave('update', spot.id, updateData);
    };

    return (
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifier le spot</DialogTitle>
          <DialogDescription>
            Modifier les informations du spot "{spot.title}"
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Titre</Label>
              <Input
                value={editData.title}
                onChange={(e) => setEditData(prev => ({...prev, title: e.target.value}))}
                required
              />
            </div>
            <div>
              <Label>Auteur</Label>
              <Input
                value={editData.author}
                onChange={(e) => setEditData(prev => ({...prev, author: e.target.value}))}
              />
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={editData.description}
              onChange={(e) => setEditData(prev => ({...prev, description: e.target.value}))}
              rows={3}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Latitude du spot</Label>
              <Input
                type="number"
                step="any"
                value={editData.latitude}
                onChange={(e) => setEditData(prev => ({...prev, latitude: parseFloat(e.target.value)}))}
                required
              />
            </div>
            <div>
              <Label>Longitude du spot</Label>
              <Input
                type="number"
                step="any"
                value={editData.longitude}
                onChange={(e) => setEditData(prev => ({...prev, longitude: parseFloat(e.target.value)}))}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Latitude parking (optionnel)</Label>
              <Input
                type="number"
                step="any"
                value={editData.parking_latitude}
                onChange={(e) => setEditData(prev => ({...prev, parking_latitude: e.target.value}))}
              />
            </div>
            <div>
              <Label>Longitude parking (optionnel)</Label>
              <Input
                type="number"
                step="any"
                value={editData.parking_longitude}
                onChange={(e) => setEditData(prev => ({...prev, parking_longitude: e.target.value}))}
              />
            </div>
          </div>

          <div>
            <Label>Tags (séparés par des virgules)</Label>
            <Input
              value={editData.tags}
              onChange={(e) => setEditData(prev => ({...prev, tags: e.target.value}))}
              placeholder="lac, montagne, coucher de soleil"
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button 
              type="submit" 
              disabled={actionLoading}
              className="flex-1"
            >
              {actionLoading ? 'En cours...' : 'Sauvegarder'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setEditingSpot(null)}>
              Annuler
            </Button>
          </div>
        </form>
      </DialogContent>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Spots</h1>
          <p className="text-gray-600 mt-1">Gérer tous les spots de la plateforme</p>
        </div>
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-green-600" />
          <span className="font-medium text-green-600">{spots.length} spots</span>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par titre, description ou auteur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-full sm:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="active">Actifs</SelectItem>
                  <SelectItem value="inactive">Inactifs</SelectItem>
                  <SelectItem value="under_review">En révision</SelectItem>
                  <SelectItem value="deleted">Supprimés</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Spots Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Spots</CardTitle>
          <CardDescription>
            Cliquez sur un spot pour voir les détails et effectuer des actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 border-3 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                <span>Chargement des spots...</span>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {spots.map((spot) => (
                <div
                  key={spot.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    {/* Spot Image Preview */}
                    <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-blue-500 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                      {spot.photos && spot.photos.length > 0 ? (
                        <img 
                          src={spot.photos[0].url || spot.photos[0]} 
                          alt={spot.title}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <MapPin className="h-8 w-8" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900 truncate">{spot.title}</h3>
                        {getStatusBadge(spot.spot_status || 'active')}
                        {spot.is_anonymous && (
                          <Badge variant="outline" className="text-xs">Anonyme</Badge>
                        )}
                      </div>

                      <p className="text-sm text-gray-600 truncate mb-2">{spot.description}</p>

                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {spot.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(spot.created_at).toLocaleDateString('fr-FR')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Navigation className="h-3 w-3" />
                          {spot.latitude.toFixed(4)}, {spot.longitude.toFixed(4)}
                        </span>
                        {spot.parking_latitude && (
                          <span className="flex items-center gap-1">
                            <Car className="h-3 w-3" />
                            Parking
                          </span>
                        )}
                      </div>

                      {/* Tags */}
                      {spot.tags && spot.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {spot.tags.slice(0, 3).map((tag, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {spot.tags.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{spot.tags.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Stats */}
                    <div className="text-right text-sm text-gray-600 flex-shrink-0">
                      <div className="flex items-center gap-1">
                        <Heart className="h-3 w-3" />
                        {spot.likes || 0}
                      </div>
                      <div className="flex items-center gap-1">
                        <MessageCircle className="h-3 w-3" />
                        {spot.comments?.length || 0}
                      </div>
                      <div className="flex items-center gap-1">
                        <ImageIcon className="h-3 w-3" />
                        {spot.photos?.length || 0}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingSpot(spot)}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedSpot(spot);
                          setShowSpotModal(true);
                        }}
                      >
                        <MoreHorizontal className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}

              {spots.length === 0 && (
                <div className="text-center py-8">
                  <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Aucun spot trouvé</p>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              variant="outline"
              disabled={currentPage === 0}
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
            >
              Précédent
            </Button>
            <span className="text-sm text-gray-600">
              Page {currentPage + 1}
            </span>
            <Button
              variant="outline"
              disabled={spots.length < spotsPerPage}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              Suivant
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <Dialog open={showSpotModal} onOpenChange={setShowSpotModal}>
        {selectedSpot && (
          <SpotActionModal 
            spot={selectedSpot} 
            onAction={handleSpotAction}
          />
        )}
      </Dialog>

      <Dialog open={!!editingSpot} onOpenChange={(open) => !open && setEditingSpot(null)}>
        {editingSpot && (
          <SpotEditModal 
            spot={editingSpot} 
            onSave={handleSpotAction}
          />
        )}
      </Dialog>
    </div>
  );
}